import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  PatientProcedureRepositoryService,
  PracticePatientProcedureEntry,
  PracticePatientProcedureField
} from '@xmedical/core';
import { PatientStore } from './patient.store';
import { PracticeStore } from './practice.store';
/**
 * PatientProceduresStore — entries for the current patient's per-procedure
 * data tables, keyed by procedureId.
 *
 * Behaviour differs from the legacy PatientService.loadPatientProcedureEntries:
 *
 *   - On-demand load instead of cross-procedure fan-out. The legacy
 *     pre-load was intentional (so opening a procedure page rendered
 *     instantly) but lazy — it forkJoin'd a load over EVERY practice
 *     procedure on every patient hydrate, even though the procedure
 *     page only ever displays one. The store loads via loadFor() at
 *     route-activation time instead.
 *   - Auto-load when the practice has exactly one configured procedure.
 *     Most practices fall in this bucket, so they keep the instant-render
 *     UX without paying the N-call cost. Multi-procedure practices defer
 *     the fetch until the user opens a procedure page.
 *   - Save reloads only the affected key, not every procedure.
 *
 * loadFor() is idempotent for the current patient — it skips if entries for
 * that procedureId are already present. reloadFor() always re-fetches.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientProceduresStore {
  private patientStore = inject(PatientStore);
  private practiceStore = inject(PracticeStore);
  private repo = inject(PatientProcedureRepositoryService);

  private readonly _entries = signal<Map<number, PracticePatientProcedureEntry[]>>(new Map());
  private readonly _loading = signal<Set<number>>(new Set());
  private readonly _error = signal<string | null>(null);

  readonly entriesMap = this._entries.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._entries().size);

  /** Entries for a given procedure id, or [] if not loaded. */
  entriesFor(procedureId: number): PracticePatientProcedureEntry[] {
    return this._entries().get(procedureId) ?? [];
  }

  /** True while a fetch for this procedureId is in-flight. */
  isLoading(procedureId: number): boolean {
    return this._loading().has(procedureId);
  }

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      if (!patient) {
        this._entries.set(new Map());
        this._loading.set(new Set());
        this._error.set(null);
        return;
      }
      // Single-procedure practices get the entries pre-warmed (preserves
      // the legacy instant-render UX for the common case).
      const procedures = this.practiceStore.practiceProcedures();
      if (procedures.length === 1) {
        this.fetch(procedures[0].id, patient.id);
      }
    });
  }

  /**
   * Idempotent load — fetches only if entries for this procedureId are not
   * already present for the current patient. Use this from route activation.
   */
  loadFor(procedureId: number): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    if (this._entries().has(procedureId)) return;
    this.fetch(procedureId, patient.id);
  }

  /**
   * Always re-fetches. Use after a mutation or for a manual refresh.
   */
  reloadFor(procedureId: number): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(procedureId, patient.id);
  }

  /**
   * Save a new procedure entry and reload that procedure's entries on
   * success. Returns the create-response observable so the caller can react
   * to errors.
   */
  saveEntry(procedureId: number, fields: PracticePatientProcedureField[]): Observable<unknown> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientProceduresStore.saveEntry() called with no current patient');
    }
    return this.repo.saveEntry(procedureId, patient.id, fields).pipe(
      tap({
        next: () => this.reloadFor(procedureId)
      })
    );
  }

  private fetch(procedureId: number, patientId: number): void {
    // Use update() so the read happens in a non-reactive context — otherwise
    // calling fetch() from inside the constructor's effect would track
    // _loading as a dependency, and the subsequent set() would trigger an
    // infinite re-run.
    this._loading.update(prev => {
      const next = new Set(prev);
      next.add(procedureId);
      return next;
    });
    this._error.set(null);
    this.repo.loadEntries(procedureId, patientId).subscribe({
      next: entries => {
        this._entries.update(prev => {
          const next = new Map(prev);
          next.set(procedureId, entries);
          return next;
        });
        this._loading.update(prev => {
          const next = new Set(prev);
          next.delete(procedureId);
          return next;
        });
      },
      error: error => {
        this._loading.update(prev => {
          const next = new Set(prev);
          next.delete(procedureId);
          return next;
        });
        this._error.set('Could not load procedure entries');
        
      }
    });
  }
}
